import { useContext, useMemo, useState, useRef } from 'react';
import styles from './CompositionRowValue.module.css';
import ThemeContext from 'config/context/themeContext';
import dataTypesConfig from '../../constants/dataTypes.json';
import { converterFor } from '../../utils/typeConverters';

const CompositionRowValue = ({ value, type, scale }) => {
    const { dark } = useContext(ThemeContext);
    const [open, setOpen] = useState(false);
    const popoverRef = useRef(null);
    
    // Get converter for current type
    // `scale` postoji samo za tačne decimale — bez njega je ovo zatečeni
    // konvertor po tipu.
    const converter = useMemo(() => converterFor(type, scale), [type, scale]);

    // Handle boolean type specially
    if (type === 'bool') {
        return (
            <div className={`${styles.boolean_value} ${value ? styles.boolean_value_true : ''} ${dark ? styles.dark : ''}`}></div>
        );
    }

    // Handle time_point type with custom formatting
    //
    // Reordered as text, never through `new Date()`. Date reinterprets the
    // string in the browser's timezone and cannot hold a year outside roughly
    // +/-285 from the epoch, while the stored type spans +/-294,000. The value
    // arrives from the node already in UTC and needs no conversion, only
    // rearranging.
    //
    // The previous version did go through Date and lost two things: BC dates
    // and years past 9999 became Invalid Date and rendered as an empty cell,
    // and everything past the minute was dropped, so two rows a second apart
    // looked identical.
    if (type === dataTypesConfig.supportedTypes.TIME_POINT) {
        const full = converter.displayFull ? converter.displayFull(value) : '';
        const parts = full.match(/^(-?)(\d{4,6})-(\d{2})-(\d{2})T(\d{2}:\d{2}:\d{2}(?:\.\d+)?)$/);
        if (parts) {
            const [, sign, year, month, day, time] = parts;
            const era = sign === '-' ? ' BC' : '';
            // The node pads BC years to six digits ("-000044"); keep four.
            const shortYear = year.replace(/^0+(?=\d{4})/, '');
            return (
                <span
                    className={`${styles.value} ${dark ? styles.dark : ''}`}
                    title={String(value)}
                >
                    {`${day}.${month}.${shortYear}${era} ${time}`}
                </span>
            );
        }
        // Unrecognised shape: show it verbatim rather than an empty cell, so a
        // value that is on the chain stays visible.
        if (value !== null && value !== undefined && value !== '') {
            return (
                <span className={`${styles.value} ${dark ? styles.dark : ''}`}>
                    {String(value)}
                </span>
            );
        }
    }

    // Object/Array preview (for nested values)
    if (value !== null && typeof value === 'object') {
        const isArrayValue = Array.isArray(value);
        const summary = isArrayValue ? `[${value.length}]` : '{…}';
        const pretty = (() => {
            try { return JSON.stringify(value, null, 2); } catch { return '{}'; }
        })();

        return (
            <div className={`${styles.value_object} ${dark ? styles.dark : ''}`}>
                <button type="button" className={styles.json_trigger} onClick={() => setOpen(!open)}>
                    {isArrayValue ? 'View list' : 'View object'} {summary}
                </button>
                {open && (
                    <div className={styles.json_popover} ref={popoverRef}>
                        <pre className={styles.json_pre}>{pretty}</pre>
                        <div className={styles.json_actions}>
                            <button className={styles.ghost} type="button" onClick={() => setOpen(false)}>Close</button>
                            <button className={styles.primary} type="button" onClick={async () => {
                                try { await navigator.clipboard.writeText(pretty); } catch {}
                            }}>Copy</button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // For all other types, use converter to display
    const displayValue = converter.toDisplay(value);
    return (
        <span className={`${styles.value} ${dark ? styles.dark : ''}`}>
            {displayValue}
        </span>
    );
}

export default CompositionRowValue;